import { useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { C, font } from "../tokens";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FaqSection from "../components/FaqSection";
import NotFound from "./NotFound";
import { applySeo, SITE_URL } from "../lib/seo";
import { featuresBySlug, features } from "../data/features";
import appStoreSvg from "../assets/site/app-store.svg";
import googlePlaySvg from "../assets/site/google-play.svg";
import "../styles/blog.css";

const APP_STORE_URL = "https://apps.apple.com/us/app/imobmeet/id6759934477";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.imobmeet.imobmeetapp&hl=pt_BR";

// Logos em formato "wordmark" (largos): recebem altura menor e largura máxima
// para não ficarem desproporcionais ao lado dos ícones quadrados.
export const WIDE_LOGOS = new Set(["d4sign", "zapsign", "informakon", "konstroi", "morada", "hubspot", "totvs", "mega", "conta-azul"]);
export const logoClass = (name) => (WIDE_LOGOS.has(name) ? "wide" : "");

// ─── Helpers de animação (também usados por outras páginas, ex.: Integracoes) ──

// Reveal on scroll: usa as classes globais .reveal/.is-in/.delay-N do site-real.css
export function Reveal({ children, delay = 0, as: Tag = "div", className = "", style, ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([m]) => { if (m.isIntersecting) { el.classList.add("is-in"); io.disconnect(); } },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Tag ref={ref} className={`reveal${delay ? ` delay-${delay}` : ""} ${className}`} style={style} {...rest}>
      {children}
    </Tag>
  );
}

// Ícone do chip: animado por tipo (whatsapp, meta, kanban, avatar) ou bolinha colorida
function ChipIcon({ c }) {
  const tone = c.tone === "success" ? C.success : c.tone === "warning" ? C.warning : C.accent;
  if (c.icon === "whatsapp")
    return (
      <span className="chip-ic chip-wa" aria-hidden="true">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 2a8 8 0 1 1-4.2 14.8l-.3-.2-3 .8.8-2.9-.2-.3A8 8 0 0 1 12 4zm-3 4.5c-.2 0-.5 0-.7.3-.3.3-1 1-1 2.4s1 2.8 1.2 3c.1.2 2 3.2 5 4.4 2.4 1 2.9.8 3.4.7.5 0 1.7-.7 1.9-1.4.2-.7.2-1.2.2-1.4l-.5-.3-1.9-.9c-.3-.1-.4-.1-.6.1l-.8 1c-.2.2-.3.2-.6.1-.3-.1-1.2-.4-2.3-1.4-.8-.8-1.4-1.7-1.6-2-.2-.3 0-.4.1-.6l.4-.5.3-.5c.1-.2 0-.4 0-.5L9.8 9c-.2-.4-.4-.4-.6-.4H9z" /></svg>
      </span>
    );
  if (c.icon === "meta")
    return <span className="chip-ic chip-meta" aria-hidden="true"><img src="/assets/integ/facebook-ads.png" alt="" /></span>;
  if (c.icon === "kanban")
    return <span className="chip-kanban" aria-hidden="true"><i className="col"><b className="card" /></i><i className="col" /></span>;
  if (c.icon === "avatar")
    return <span className="chip-avatar" aria-hidden="true">{c.initials || "•"}</span>;
  return <span className="dot" style={{ background: tone }} />;
}

// Palco 3D: brilho colorido atrás + tilt que segue o mouse (só em dispositivos com hover)
export function Stage({ children, glow, chips }) {
  const ref = useRef(null);
  const wrapRef = useRef(null);
  // Janelas baixas (banners curtos): chips vão para fora da imagem para não cobrir o conteúdo
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => {
      el.classList.toggle("compact", e.contentRect.height < 300);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const onMove = (e) => {
    const el = ref.current;
    if (!el || !window.matchMedia("(hover: hover)").matches) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `rotateY(${(x * 7).toFixed(2)}deg) rotateX(${(-y * 7).toFixed(2)}deg)`;
  };
  const onLeave = () => { if (ref.current) ref.current.style.transform = ""; };
  return (
    <div ref={wrapRef} className="feat-stage" onMouseMove={onMove} onMouseLeave={onLeave}>
      <div className="feat-glow" style={{ background: glow }} />
      <div ref={ref} className="feat-tilt">{children}</div>
      {chips && chips.map((c, i) => (
        <span
          key={i}
          className={`feat-chip ${c.pos || "tl"}`}
          style={{ fontFamily: font, animationDelay: `${0.5 + i * 0.35}s, ${i * 0.7}s` }}
        >
          <ChipIcon c={c} />
          {c.label}
        </span>
      ))}
    </div>
  );
}

// Ícones dos tiles de destaque (escolhidos por `bulletIcons` no data; senão, ciclam)
const TILE_ICONS = {
  inbox: <><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></>,
  shuffle: <path d="M16 3h5v5M4 20 21 3M21 16v5h-5M15 15l6 6M4 4l5 5" />,
  kanban: <><rect x="3" y="3" width="5" height="18" rx="1" /><rect x="10" y="3" width="5" height="12" rx="1" /><rect x="17" y="3" width="4" height="8" rx="1" /></>,
  flame: <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />,
  chart: <path d="M3 3v18h18M7 14l4-4 4 4 5-6" />,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>,
  doc: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M9 13h6M9 17h6" /></>,
  bell: <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></>,
  shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" /></>,
  map: <><path d="M1 6v16l7-4 8 4 7-4V2l-7 4-8-4z" /><path d="M8 2v16M16 6v16" /></>,
  money: <><rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="3" /><path d="M6 12h.01M18 12h.01" /></>,
  pen: <><path d="M3 17c3-1 4-4 6-4s2 2 4 2 3-3 5-3" /><path d="M3 21h18" /></>,
  bolt: <path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" />,
  chat: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  spark: <><path d="M12 3v2M12 19v2M5 12H3M21 12h-2M6 6l1.5 1.5M16.5 16.5 18 18M18 6l-1.5 1.5M7.5 16.5 6 18" /><circle cx="12" cy="12" r="4" /></>,
};
const TILE_CYCLE = ["bolt", "shuffle", "kanban", "clock", "chart", "users", "doc", "shield"];
export const TileIcon = ({ name }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    {TILE_ICONS[name] || TILE_ICONS.bolt}
  </svg>
);

const Check = ({ color = "#10B981", size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

// Janela com várias telas alternando em crossfade (ex.: incorporadoras ⇄ loteadoras).
const AppWindowSlides = ({ shots, alt }) => {
  const n = shots.length;
  const dur = n * 5; // 5s por tela
  return (
    <div className="feat-window">
      <div className="feat-window-bar" style={{ justifyContent: "space-between" }}>
        <span style={{ display: "flex", gap: 6 }}>
          <i style={{ background: "#FF5F57" }} /><i style={{ background: "#FEBC2E" }} /><i style={{ background: "#28C840" }} />
        </span>
        <span className="feat-slide-titles" style={{ fontFamily: font }}>
          {shots.map((s, i) => (
            <span key={i} className="feat-slide-title" style={{ animationDuration: `${dur}s`, animationDelay: `${i * 5}s` }}>{s.title}</span>
          ))}
        </span>
        <span style={{ width: 40 }} />
      </div>
      <div className="feat-slides">
        <img src={shots[0].src} alt={alt} decoding="async" style={{ visibility: "hidden" }} />
        {shots.map((s, i) => (
          <img key={i} className="feat-slide" src={s.src} alt={i === 0 ? alt : ""} decoding="async" style={{ animationDuration: `${dur}s`, animationDelay: `${i * 5}s` }} />
        ))}
      </div>
    </div>
  );
};

// Janela de app em volta de um screenshot real.
const AppWindow = ({ src, alt, title }) => (
  <div className="feat-window">
    <div className="feat-window-bar" style={{ justifyContent: "space-between" }}>
      <span style={{ display: "flex", gap: 6 }}>
        <i style={{ background: "#FF5F57" }} /><i style={{ background: "#FEBC2E" }} /><i style={{ background: "#28C840" }} />
      </span>
      <span style={{ fontFamily: font, fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.7)" }}>{title || "IMOBMEET"}</span>
      <span style={{ width: 40 }} />
    </div>
    <img src={src} alt={alt} loading="lazy" decoding="async" />
  </div>
);

// Mockup animado de chat. theme "dark" (consultora de IA) ou "wa" (WhatsApp).
const CHAT_SCRIPTS = {
  ai: {
    title: "Ana · Consultora IMOBMEET",
    msgs: [
      ["them", "Oi! Aqui é a Ana, do IMOBMEET. Me conta: o que você mais precisa resolver na sua operação hoje?"],
      ["me", "Tenho muitos leads e pouco atendimento à noite."],
      ["them", "Perfeito. Eu qualifico e respondo seus leads 24h no WhatsApp e já agendo a visita no seu funil. Quer ver funcionando?"],
    ],
  },
  comercial: {
    title: "WhatsApp · Fila Comercial",
    msgs: [
      ["me", "Boa tarde! Vi o lançamento no Instagram. Ainda tem lote na quadra 10?"],
      ["them", "Boa tarde, Ana! Tem sim — separei as opções disponíveis e as condições de entrada. Posso agendar uma visita ao estande?"],
      ["me", "Pode, sábado de manhã."],
      ["them", "Agendado para sábado às 10h. Já registrei no seu atendimento e o corretor Marcos vai te receber."],
    ],
  },
  comissao: {
    title: "App do corretor · Extrato de comissões",
    msgs: [
      ["me", "Quanto tenho a receber da venda da unidade 2502?"],
      ["them", "Sua parte do split é 60% da comissão. Status: aprovada — repasse via PIX previsto para o próximo ciclo de pagamento."],
      ["me", "E o comprovante?"],
      ["them", "Assim que o repasse for feito, o comprovante e o recibo ficam disponíveis aqui no seu extrato."],
    ],
  },
  cobranca: {
    title: "WhatsApp · Financeiro",
    msgs: [
      ["them", "Olá, Ana! Sua parcela vence em 3 dias. Segue o boleto e o Pix copia e cola para pagar quando preferir."],
      ["me", "Consigo a 2ª via com o vencimento atualizado?"],
      ["them", "Claro! Gerei a 2ª via atualizada e enviei aqui mesmo. Qualquer dúvida, é só chamar."],
    ],
  },
};
const ChatMock = ({ script = "ai", theme = "dark" }) => {
  const s = CHAT_SCRIPTS[script] || CHAT_SCRIPTS.ai;
  const wa = theme === "wa";
  const bg = wa ? "#E9E3DA" : "#0B1224";
  const themBg = wa ? "#FFFFFF" : "#182742";
  const themColor = wa ? "#0A0F1E" : "#E7EEFB";
  const meBg = wa ? "#D9FDD3" : C.accent;
  const meColor = wa ? "#0A0F1E" : "#fff";
  return (
    <div className="feat-window" style={{ background: bg }}>
      <div className="feat-window-bar" style={{ justifyContent: "space-between", background: wa ? "#128C7E" : undefined }}>
        <span style={{ display: "flex", gap: 6 }}>
          <i style={{ background: "#FF5F57" }} /><i style={{ background: "#FEBC2E" }} /><i style={{ background: "#28C840" }} />
        </span>
        <span style={{ fontFamily: font, fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.85)" }}>{s.title}</span>
        <span style={{ width: 40 }} />
      </div>
      <div style={{ padding: "22px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
        {s.msgs.map(([side, text], i) => (
          <div
            key={i}
            className="ai-bubble"
            style={{
              alignSelf: side === "me" ? "flex-end" : "flex-start", maxWidth: "82%",
              background: side === "me" ? meBg : themBg, color: side === "me" ? meColor : themColor,
              fontFamily: font, fontSize: 14, lineHeight: 1.5, padding: "11px 14px",
              borderRadius: side === "me" ? "16px 4px 16px 16px" : "4px 16px 16px 16px",
              boxShadow: wa ? "0 1px 2px rgba(0,0,0,.08)" : "none", animationDelay: `${0.1 + i * 0.8}s`,
            }}
          >
            {text}
          </div>
        ))}
        <div className="ai-bubble ai-typing" style={{ alignSelf: "flex-start", display: "inline-flex", gap: 5, background: themBg, padding: "13px 16px", borderRadius: 16, animationDelay: `${0.1 + s.msgs.length * 0.8}s` }}>
          <i /><i /><i />
        </div>
      </div>
    </div>
  );
};
const AiChatMock = () => <ChatMock script="ai" theme="dark" />;

// Mockup animado do fluxo de assinatura digital (loop de 8s).
const SIGN_LOGOS = ["docusign", "clicksign", "zapsign", "d4sign"];
const SignFlowMock = () => (
  <div className="feat-window">
    <div className="feat-window-bar" style={{ justifyContent: "space-between" }}>
      <span style={{ display: "flex", gap: 6 }}>
        <i style={{ background: "#FF5F57" }} /><i style={{ background: "#FEBC2E" }} /><i style={{ background: "#28C840" }} />
      </span>
      <span style={{ fontFamily: font, fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.85)" }}>Contrato · Assinatura digital</span>
      <span style={{ width: 40 }} />
    </div>
    <div className="sf-body">
      <div className="sf-doc">
        <div className="sf-doc-h" />
        <div className="sf-line" /><div className="sf-line short" /><div className="sf-line" /><div className="sf-line short" /><div className="sf-line" />
        <svg className="sf-sig" viewBox="0 0 160 50" aria-hidden="true">
          <path d="M6 36 C 22 4, 38 48, 58 24 S 92 2, 108 30 S 138 44, 154 18" />
        </svg>
        <span className="sf-stamp" style={{ fontFamily: font }}>Assinado ✓</span>
      </div>
      <div className="sf-steps" style={{ fontFamily: font }}>
        {["Documento gerado do modelo", "Signatários definidos", "Enviado para assinatura", "Assinado com validade jurídica"].map((t, i) => (
          <div key={i} className={`sf-step s${i + 1}`}>
            <span className="sf-dot">{i + 1}</span>
            <span>{t}</span>
          </div>
        ))}
        <div className="sf-bar"><i /></div>
        <div className="sf-logos">
          {SIGN_LOGOS.map((l) => <img key={l} className={logoClass(l)} src={`/assets/integ/${l}.png`} alt={l} loading="lazy" decoding="async" />)}
        </div>
      </div>
    </div>
  </div>
);

// Mockup animado do ciclo do boleto (loop de 10s):
// venda ganha → parcelas geradas → boleto emitido → enviado no WhatsApp → pagamento conciliado.
const BF_STAGES = ["Gerada", "Boleto emitido", "Enviado", "Pago ✓"];
const BoletoFlowMock = () => (
  <div className="feat-window">
    <div className="feat-window-bar" style={{ justifyContent: "space-between" }}>
      <span style={{ display: "flex", gap: 6 }}>
        <i style={{ background: "#FF5F57" }} /><i style={{ background: "#FEBC2E" }} /><i style={{ background: "#28C840" }} />
      </span>
      <span style={{ fontFamily: font, fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.85)" }}>Contas a Receber · Contrato #2847</span>
      <span style={{ width: 40 }} />
    </div>
    <div className="bf-body" style={{ fontFamily: font }}>
      <div className="bf-list">
        <div className="bf-list-h"><span>Parcela</span><span>Vencimento</span><span>Status</span></div>
        {[["Entrada", "10/10"], ["Parcela 1/60", "10/11"], ["Parcela 2/60", "10/12"]].map(([p, d], i) => (
          <div key={i} className="bf-row" style={{ animationDelay: `${i * 0.35}s` }}>
            <span className="bf-p">{p}</span>
            <span className="bf-d">{d}</span>
            <span className="bf-pills">
              {BF_STAGES.map((s, k) => (
                <span key={k} className={`bf-pill k${k + 1}`} style={{ animationDelay: `${i * 0.35}s` }}>{s}</span>
              ))}
            </span>
          </div>
        ))}
        <div className="bf-steps">
          {["Venda ganha", "Boleto emitido", "Enviado", "Conciliado"].map((t, i) => (
            <span key={i} className={`bf-step s${i + 1}`}><i /> {t}</span>
          ))}
        </div>
        <div className="sf-bar"><i style={{ animationDuration: "10s" }} /></div>
      </div>
      <div className="bf-doc-wrap">
        <div className="bf-doc">
          <div className="bf-doc-top"><span className="bf-bank" /><span className="bf-bank-t">Boleto bancário</span></div>
          <div className="bf-doc-lines"><i /><i style={{ width: "60%" }} /><i style={{ width: "80%" }} /></div>
          <div className="bf-amount"><span>Valor</span><b>R$ 2.058,48</b></div>
          <div className="bf-barcode" aria-hidden="true">
            {Array.from({ length: 34 }).map((_, i) => <i key={i} style={{ animationDelay: `${i * 0.03}s`, width: i % 3 === 0 ? 3 : i % 5 === 0 ? 4 : 2 }} />)}
          </div>
          <span className="bf-stamp">PAGO ✓</span>
          <span className="bf-send" style={{ fontFamily: font }}>
            <i className="bf-send-ic">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="#fff"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 2a8 8 0 1 1-4.2 14.8l-.3-.2-3 .8.8-2.9-.2-.3A8 8 0 0 1 12 4zm-3 4.5c-.2 0-.5 0-.7.3-.3.3-1 1-1 2.4s1 2.8 1.2 3c.1.2 2 3.2 5 4.4 2.4 1 2.9.8 3.4.7.5 0 1.7-.7 1.9-1.4.2-.7.2-1.2.2-1.4l-.5-.3-1.9-.9c-.3-.1-.4-.1-.6.1l-.8 1c-.2.2-.3.2-.6.1-.3-.1-1.2-.4-2.3-1.4-.8-.8-1.4-1.7-1.6-2-.2-.3 0-.4.1-.6l.4-.5.3-.5c.1-.2 0-.4 0-.5L9.8 9c-.2-.4-.4-.4-.6-.4H9z" /></svg>
            </i>
            Enviado no WhatsApp
          </span>
        </div>
      </div>
    </div>
  </div>
);

// Mockup animado do split de comissões (loop de 10s):
// venda → comissão → divisão por papel (barras) → repasse via PIX → recibos.
const SPLIT_PARTS = [["Corretor", 60, "#0560FF"], ["Imobiliária", 25, "#8B5CF6"], ["Captador", 10, "#10B981"], ["Gerente", 5, "#F59E0B"]];
const SplitFlowMock = () => (
  <div className="feat-window">
    <div className="feat-window-bar" style={{ justifyContent: "space-between" }}>
      <span style={{ display: "flex", gap: 6 }}>
        <i style={{ background: "#FF5F57" }} /><i style={{ background: "#FEBC2E" }} /><i style={{ background: "#28C840" }} />
      </span>
      <span style={{ fontFamily: font, fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.85)" }}>Split de comissões · Venda #2847</span>
      <span style={{ width: 40 }} />
    </div>
    <div className="sp-body" style={{ fontFamily: font }}>
      <div className="sp-top">
        <div className="sp-box"><span>Venda</span><b>R$ 350.000</b></div>
        <span className="sp-arr"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg></span>
        <div className="sp-box"><span>Comissão · 5%</span><b>R$ 17.500</b></div>
        <span className="sp-arr"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg></span>
        <div className="sp-box hi"><span>Split automático</span><b>4 partes</b></div>
      </div>
      <div className="sp-parts">
        {SPLIT_PARTS.map(([nm, p, c], i) => (
          <div key={nm} className="sp-part">
            <div className="sp-part-h"><span><i style={{ background: c }} />{nm}</span><b>{p}%</b></div>
            <div className="sp-bar"><i style={{ background: c, "--w": `${p}%`, animationDelay: `${i * 0.25}s` }} /></div>
            <span className="sp-pix" style={{ animationDelay: `${i * 0.3}s` }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
              PIX enviado
            </span>
          </div>
        ))}
      </div>
      <div className="sp-foot">
        <span className="sp-badge">Recibos e retenções gerados automaticamente ✓</span>
      </div>
    </div>
  </div>
);

const media = (m, title) => {
  if (!m) return null;
  if (m.mockup === "ai-chat") return <AiChatMock />;
  if (m.mockup === "boleto-flow") return <BoletoFlowMock />;
  if (m.mockup === "split-flow") return <SplitFlowMock />;
  if (m.mockup === "wa-chat") return <ChatMock script={m.script || "comercial"} theme="wa" />;
  if (m.mockup === "sign-flow") return <SignFlowMock />;
  if (m.image) return <AppWindow src={m.image} alt={m.title || title || ""} title={m.windowTitle || m.title} />;
  return null;
};

// ─── Página ──────────────────────────────────────────────────────────────────

// Uso padrão: rota /funcionalidades/:slug. Páginas de produto (ex.: /split-comissoes)
// podem reaproveitar o template passando `slug` e `path` explicitamente.
export default function FuncionalidadeDetalhe({ slug: forcedSlug, path: forcedPath }) {
  const params = useParams();
  const slug = forcedSlug || params.slug;
  const f = featuresBySlug[slug];
  const allowed = !!f && (f.ownPage || !!forcedSlug);
  const pagePath = forcedPath || `/funcionalidades/${slug}`;

  useEffect(() => {
    if (!allowed) return;
    applySeo({
      title: f.seoTitle, description: f.seoDesc, keywords: f.keywords,
      path: pagePath, image: SITE_URL + f.cover,
    });
    window.scrollTo(0, 0);
  }, [slug]); // eslint-disable-line

  if (!allowed) return <NotFound />;

  // Outras funcionalidades da categoria (páginas próprias + módulos com página de produto)
  const others = features.filter((x) => x.category === f.category && x.slug !== f.slug && (x.ownPage || x.externalTo));
  const hasStage = !!(f.heroShot || f.heroMockup);
  const glow = `radial-gradient(60% 60% at 50% 50%, ${f.color} 0%, ${C.accent} 55%, transparent 100%)`;

  return (
    <>
      <Navbar />

      {/* ── Hero ── */}
      <section
        className="feat-hero"
        style={{ background: "linear-gradient(135deg, #0B1733 0%, #0A1230 100%)", padding: hasStage ? "108px 28px 160px" : "108px 28px 64px" }}
      >
        <div className="feat-gridanim" />
        <div className="feat-orb" style={{ width: 520, height: 520, background: C.accent, top: -180, left: -140 }} />
        <div className="feat-orb b" style={{ width: 460, height: 460, background: f.color, top: -120, right: -120 }} />
        <div style={{ maxWidth: 900, margin: "0 auto", position: "relative", textAlign: "center" }}>
          <div className="feat-rise" style={{ animationDelay: "0.05s" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: font, fontSize: 12, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: "#fff", background: f.bg, padding: "6px 14px 6px 8px", borderRadius: 999, marginBottom: 22, boxShadow: `0 10px 30px ${f.color}55` }}>
              <span style={{ width: 22, height: 22, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{f.icon}</span>
              {f.category}
            </span>
          </div>
          <h1 className="feat-rise" style={{ fontFamily: font, fontSize: "clamp(32px, 5vw, 52px)", fontWeight: 800, lineHeight: 1.04, letterSpacing: -1.4, color: "#fff", margin: "0 0 18px", animationDelay: "0.15s" }}>
            {f.name}
          </h1>
          <p className="feat-rise" style={{ fontFamily: font, fontSize: 18.5, lineHeight: 1.55, color: "rgba(255,255,255,0.82)", margin: "0 auto 30px", maxWidth: 700, animationDelay: "0.25s" }}>
            {f.tagline}
          </p>
          <div className="feat-rise" style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", animationDelay: "0.35s" }}>
            <Link to="/agendar-demo" style={{ fontFamily: font, fontSize: 15, fontWeight: 800, color: "#fff", background: C.accent, padding: "14px 26px", borderRadius: 12, textDecoration: "none", boxShadow: "0 10px 28px rgba(5,96,255,0.45)" }}>
              Agendar demonstração →
            </Link>
            <Link to="/funcionalidades" style={{ fontFamily: font, fontSize: 15, fontWeight: 700, color: "#fff", background: "rgba(255,255,255,0.08)", border: "1.5px solid rgba(255,255,255,0.26)", padding: "14px 26px", borderRadius: 12, textDecoration: "none", backdropFilter: "blur(6px)" }}>
              Ver todas as funcionalidades
            </Link>
          </div>
        </div>
      </section>

      {/* ── Palco do herói: janela do app com tilt + chips (sobreposto ao hero) ── */}
      {hasStage && (
        <section style={{ background: C.surface, padding: "0 28px" }}>
          <div className="feat-rise" style={{ maxWidth: 1000, margin: "-124px auto 0", position: "relative", zIndex: 2, animationDelay: "0.45s" }}>
            <Stage glow={glow} chips={f.chips}>
              {f.heroMockup
                ? media({ mockup: f.heroMockup, script: f.heroScript })
                : f.heroShots && f.heroShots.length > 1
                  ? <AppWindowSlides shots={f.heroShots} alt={f.name} />
                  : <AppWindow src={f.heroShot} alt={f.name} title={f.heroTitle || f.name} />}
            </Stage>
          </div>
        </section>
      )}

      {/* ── Bancos em destaque (ex.: Boletos e Integração Bancária) ── */}
      {f.banks && f.banks.length > 0 && (
        <section style={{ background: C.surface, padding: hasStage ? "56px 0 0" : "36px 0 0" }}>
          <Reveal style={{ textAlign: "center", marginBottom: 18, padding: "0 28px" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "center", fontFamily: font, fontSize: 12.5, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: C.muted }}>
              {f.banksTitle || "Integrado aos principais bancos"}
              {f.banksMore && (
                <span style={{ fontFamily: font, fontSize: 11.5, fontWeight: 800, letterSpacing: 0.3, textTransform: "none", color: "#fff", background: f.bg, padding: "5px 11px", borderRadius: 999 }}>{f.banksMore}</span>
              )}
            </span>
          </Reveal>
          <div style={{ padding: "0 28px" }}>
            <div className="feat-marquee banks">
              <div className="feat-marquee-track">
                {[0, 1].map((half) => (
                  <div key={half} className="feat-marquee-half" aria-hidden={half === 1}>
                    {Array.from({ length: 3 }).flatMap((_, rep) =>
                      f.banks.map((b, i) => (
                        <img key={`${rep}-${i}`} src={`/assets/banks/${b}`} alt={half === 0 && rep === 0 ? b.replace(/\.(png|svg)$/, "") : ""} loading="lazy" decoding="async" />
                      ))
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Marquee de integrações (posição padrão: logo após o hero; logosPosition:"bottom" → após os blocos) ── */}
      {f.logos && f.logos.length > 0 && f.logosPosition !== "bottom" && (
        <section style={{ background: C.surface, padding: hasStage ? "54px 0 10px" : "40px 0 10px" }}>
          <p style={{ fontFamily: font, fontSize: 12.5, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: C.muted, textAlign: "center", margin: "0 0 22px" }}>
            Conectado ao que você já usa
          </p>
          <div style={{ padding: "0 28px" }}>
            <div className="feat-marquee">
              <div className="feat-marquee-track">
                {[0, 1].map((half) => (
                  <div key={half} className="feat-marquee-half" aria-hidden={half === 1}>
                    {Array.from({ length: 4 }).flatMap((_, rep) =>
                      f.logos.map((l, i) => (
                        <img key={`${rep}-${i}`} className={logoClass(l)} src={`/assets/integ/${l}.png`} alt={half === 0 && rep === 0 ? l.replace(/-/g, " ") : ""} loading="lazy" decoding="async" />
                      ))
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Visão geral: intro + destaques (seção própria) ── */}
      <section style={{ background: C.surface, padding: hasStage ? "64px 28px 68px" : "48px 28px 64px" }}>
        {hasStage ? (
          <div style={{ maxWidth: 1120, margin: "0 auto" }}>
            <Reveal className="feat-intro-head">
              <h2 style={{ fontFamily: font, fontSize: "clamp(24px,3vw,34px)", fontWeight: 800, letterSpacing: -0.9, color: C.text, margin: "0 0 14px", lineHeight: 1.12 }}>
                {f.introTitle || `O que você ganha com ${f.name}`}
              </h2>
              <p style={{ fontFamily: font, fontSize: 17, lineHeight: 1.65, color: "#3A4A63", margin: "0 auto", maxWidth: 720 }}>
                {f.introShort || f.intro}
              </p>
            </Reveal>
            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 16, marginTop: 36 }}>
              {f.bullets.map((b, i) => {
                const icon = (f.bulletIcons && f.bulletIcons[i]) || TILE_CYCLE[i % TILE_CYCLE.length];
                return (
                  <Reveal key={i} delay={Math.min(i + 1, 6)} className="feat-card feat-tile" style={{ flex: "1 1 196px", maxWidth: 236, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 18, padding: "22px 20px 20px", position: "relative", overflow: "hidden" }}>
                    <span className="feat-tile-bar" style={{ background: f.bg }} />
                    <span style={{ width: 46, height: 46, borderRadius: 14, background: f.bg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16, boxShadow: `0 10px 24px ${f.color}44` }}>
                      <TileIcon name={icon} />
                    </span>
                    <span style={{ display: "block", fontFamily: font, fontSize: 14.5, lineHeight: 1.5, color: C.text, fontWeight: 600 }}>{b}</span>
                  </Reveal>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="feat-grid" style={{ maxWidth: 1160, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 56, alignItems: "center" }}>
            <Reveal>
              <p style={{ fontFamily: font, fontSize: 17.5, lineHeight: 1.7, color: "#29354D", margin: "0 0 26px" }}>{f.intro}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {f.bullets.map((b, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <Check />
                    <span style={{ fontFamily: font, fontSize: 15.5, lineHeight: 1.55, color: C.text, fontWeight: 500 }}>{b}</span>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={2}>
              <Stage glow={glow}><AppWindow src={f.cover} alt={f.name} title={f.name} /></Stage>
            </Reveal>
          </div>
        )}

        {!hasStage && f.related && f.related.length > 0 && (
          <Reveal style={{ maxWidth: 1080, margin: "44px auto 0", display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: 10 }}>
            <span style={{ fontFamily: font, fontSize: 13, fontWeight: 700, color: C.muted, marginRight: 4 }}>Faz parte de:</span>
            {f.related.map((r) => (
              <Link key={r.to} to={r.to} className="feat-card" style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: font, fontSize: 14, fontWeight: 700, color: C.text, background: C.surface, border: `1px solid ${C.border}`, padding: "9px 14px 9px 12px", borderRadius: 999, textDecoration: "none" }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: f.bg, flexShrink: 0 }} />
                {r.label}
                <span style={{ color: C.accent }}>→</span>
              </Link>
            ))}
          </Reveal>
        )}
      </section>

      {/* ── Dores → Solução ── */}
      {f.dores && f.dores.length > 0 && (
        <section style={{ background: C.bgAlt, padding: "76px 28px", position: "relative", overflow: "hidden" }}>
          <div style={{ maxWidth: 1080, margin: "0 auto", position: "relative" }}>
            <Reveal>
              <h2 style={{ fontFamily: font, fontSize: "clamp(26px,3.4vw,36px)", fontWeight: 800, letterSpacing: -0.9, color: C.text, textAlign: "center", margin: "0 0 8px" }}>
                O que a {f.name.toLowerCase()} resolve
              </h2>
              <p style={{ fontFamily: font, fontSize: 16, color: C.muted, textAlign: "center", margin: "0 auto 40px", maxWidth: 560 }}>
                Do problema do dia a dia à solução dentro do IMOBMEET.
              </p>
            </Reveal>
            <Reveal className="feat-vs" style={{ maxWidth: 1040, margin: "0 auto" }}>
              {/* Cabeçalhos */}
              <div className="feat-vs-cell before head">
                <span className="feat-vs-tag" style={{ color: C.danger, background: "#FEF2F2", border: "1px solid #FECACA" }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.danger }} /> Sem o IMOBMEET
                </span>
              </div>
              <div className="feat-vs-cell mid head" />
              <div className="feat-vs-cell after head">
                <span className="feat-vs-tag" style={{ color: "#fff", background: f.bg, boxShadow: `0 8px 22px ${f.color}66` }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 6.6L21 11l-6.6 2.4L12 20l-2.4-6.6L3 11l6.6-2.4z" /></svg>
                  Com o IMOBMEET
                </span>
              </div>
              {/* Linhas */}
              {f.dores.map((d, i) => {
                const last = i === f.dores.length - 1;
                return [
                  <div key={`b${i}`} className={`feat-vs-cell before${last ? " last" : ""}`} style={{ transitionDelay: `${i * 0.12}s` }}>
                    <span className="feat-vs-x"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg></span>
                    <p>{d.antes}</p>
                  </div>,
                  <div key={`m${i}`} className={`feat-vs-cell mid${last ? " last" : ""}`}>
                    <span className="feat-vs-arrow" style={{ background: f.bg, animationDelay: `${i * 0.25}s` }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                    </span>
                  </div>,
                  <div key={`a${i}`} className={`feat-vs-cell after${last ? " last" : ""}`} style={{ transitionDelay: `${0.1 + i * 0.12}s` }}>
                    <span className="feat-vs-ok"><Check size={13} color="#fff" /></span>
                    <p>{d.depois}</p>
                  </div>,
                ];
              })}
            </Reveal>
          </div>
        </section>
      )}

      {/* ── Blocos de funcionalidade (alternados, com tilt + chips) ── */}
      {f.blocks && f.blocks.length > 0 && (
        <section style={{ background: C.surface, padding: "84px 28px 30px" }}>
          <div style={{ maxWidth: 1120, margin: "0 auto", display: "flex", flexDirection: "column", gap: 84 }}>
            {f.blocks.map((bl, i) => {
              const reverse = i % 2 === 1;
              const txt = (
                <Reveal>
                  <span style={{ display: "inline-block", fontFamily: font, fontSize: 13, fontWeight: 800, letterSpacing: 1.2, color: f.color, marginBottom: 12 }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 style={{ fontFamily: font, fontSize: "clamp(23px,2.8vw,30px)", fontWeight: 800, letterSpacing: -0.7, color: C.text, margin: "0 0 14px", lineHeight: 1.15 }}>{bl.title}</h3>
                  <p style={{ fontFamily: font, fontSize: 16.5, lineHeight: 1.7, color: "#3A4A63", margin: "0 0 18px" }}>{bl.text}</p>
                  {bl.bullets && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                      {bl.bullets.map((b, j) => (
                        <div key={j} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                          <Check />
                          <span style={{ fontFamily: font, fontSize: 15, lineHeight: 1.5, color: C.text, fontWeight: 500 }}>{b}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </Reveal>
              );
              const vis = (
                <Reveal delay={2} className="feat-media">
                  <Stage glow={glow} chips={bl.chips}>{media(bl, f.name)}</Stage>
                </Reveal>
              );
              return (
                <div key={i} className="feat-block" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 56 }}>
                  {reverse ? <>{vis}{txt}</> : <>{txt}{vis}</>}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Marquee de integrações (variante no fim, após os blocos) ── */}
      {f.logos && f.logos.length > 0 && f.logosPosition === "bottom" && (
        <section style={{ background: C.surface, padding: "34px 0 44px" }}>
          <p style={{ fontFamily: font, fontSize: 12.5, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: C.muted, textAlign: "center", margin: "0 0 22px" }}>
            Conectado ao que você já usa
          </p>
          <div style={{ padding: "0 28px" }}>
            <div className="feat-marquee">
              <div className="feat-marquee-track">
                {[0, 1].map((half) => (
                  <div key={half} className="feat-marquee-half" aria-hidden={half === 1}>
                    {Array.from({ length: 4 }).flatMap((_, rep) =>
                      f.logos.map((l, i) => (
                        <img key={`${rep}-${i}`} className={logoClass(l)} src={`/assets/integ/${l}.png`} alt={half === 0 && rep === 0 ? l.replace(/-/g, " ") : ""} loading="lazy" decoding="async" />
                      ))
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── No app do corretor (Android e iOS) ── */}
      {f.app && f.app.shots && f.app.shots.length > 0 && (
        <section style={{ background: C.bgAlt, padding: "72px 28px" }}>
          <div className="feat-app" style={{ maxWidth: 1120, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1.15fr", gap: 48, alignItems: "center" }}>
            <Reveal>
              <span style={{ display: "inline-block", fontFamily: font, fontSize: 11.5, fontWeight: 800, letterSpacing: 1.1, textTransform: "uppercase", color: f.color, marginBottom: 12 }}>
                No app do corretor · Android e iOS
              </span>
              <h2 style={{ fontFamily: font, fontSize: "clamp(24px,3vw,34px)", fontWeight: 800, letterSpacing: -0.8, color: C.text, margin: "0 0 14px", lineHeight: 1.12 }}>
                {f.app.title || "Tudo isso no celular do corretor"}
              </h2>
              {f.app.text && (
                <p style={{ fontFamily: font, fontSize: 16.5, lineHeight: 1.7, color: "#3A4A63", margin: "0 0 18px" }}>{f.app.text}</p>
              )}
              {f.app.bullets && (
                <div style={{ display: "flex", flexDirection: "column", gap: 11, marginBottom: 24 }}>
                  {f.app.bullets.map((b, j) => (
                    <div key={j} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                      <Check />
                      <span style={{ fontFamily: font, fontSize: 15, lineHeight: 1.5, color: C.text, fontWeight: 500 }}>{b}</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label="Baixar na App Store" className="feat-card" style={{ display: "inline-flex", borderRadius: 10 }}>
                  <img src={appStoreSvg} alt="Disponível na App Store" style={{ height: 44, display: "block" }} />
                </a>
                <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" aria-label="Baixar no Google Play" className="feat-card" style={{ display: "inline-flex", borderRadius: 10 }}>
                  <img src={googlePlaySvg} alt="Disponível no Google Play" style={{ height: 44, display: "block" }} />
                </a>
              </div>
            </Reveal>
            <Reveal delay={2} className="feat-phones">
              {f.app.shots.slice(0, 3).map((s, i) => (
                <img key={i} className="feat-phone" src={s} alt={`${f.name} no app IMOBMEET`} loading="lazy" decoding="async" />
              ))}
            </Reveal>
          </div>
        </section>
      )}

      {/* ── Como funciona: pipeline animado ── */}
      {f.steps && f.steps.length > 0 && (
        <section style={{ background: C.surface, padding: "56px 28px 84px" }}>
          <Reveal className="feat-pipe-wrap" style={{ maxWidth: 1160, margin: "0 auto" }}>
            <div className="feat-gridanim" />
            <div className="feat-orb" style={{ width: 420, height: 420, background: C.accent, top: -200, left: -120, opacity: .45 }} />
            <div className="feat-orb b" style={{ width: 380, height: 380, background: f.color, bottom: -220, right: -100, opacity: .4 }} />
            <div style={{ position: "relative", textAlign: "center", marginBottom: 44 }}>
              <span style={{ display: "inline-block", fontFamily: font, fontSize: 11.5, fontWeight: 800, letterSpacing: 1.2, textTransform: "uppercase", color: "#9DBBFF", background: "rgba(5,96,255,0.18)", border: "1px solid rgba(157,187,255,0.25)", padding: "6px 12px", borderRadius: 999, marginBottom: 14 }}>
                Passo a passo
              </span>
              <h2 style={{ fontFamily: font, fontSize: "clamp(24px,3vw,34px)", fontWeight: 800, letterSpacing: -0.8, color: "#fff", margin: 0 }}>
                Como funciona
              </h2>
            </div>
            <div className="feat-pipe dark" style={{ gridTemplateColumns: `repeat(${f.steps.length}, 1fr)` }}>
              <div className="feat-pipe-line" style={{ left: `${100 / (2 * f.steps.length)}%`, right: `${100 / (2 * f.steps.length)}%` }}>
                <span className="feat-pipe-glow" />
              </div>
              {f.steps.map((s, i) => (
                <div key={i} className="feat-pipe-card" style={{ textAlign: "center" }}>
                  <div className="feat-pipe-dot" style={{ background: f.bg, fontFamily: font, animationDelay: `${i * 0.45}s` }}>{i + 1}</div>
                  <div className="feat-glass">
                    <span className="feat-glass-num" style={{ fontFamily: font }}>{String(i + 1).padStart(2, "0")}</span>
                    {s.icon && (
                      <span style={{ width: 44, height: 44, borderRadius: 13, background: f.bg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14, boxShadow: `0 10px 26px ${f.color}66` }}>
                        <TileIcon name={s.icon} />
                      </span>
                    )}
                    <h3 style={{ fontFamily: font, fontSize: 16, fontWeight: 800, color: "#fff", margin: "0 0 6px", lineHeight: 1.25 }}>{s.title}</h3>
                    <p style={{ fontFamily: font, fontSize: 14, lineHeight: 1.55, color: "rgba(255,255,255,0.72)", margin: 0 }}>{s.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </section>
      )}

      {/* ── FAQ (visível + schema FAQPage) ── */}
      {f.faq && f.faq.length > 0 && <FaqSection items={f.faq} />}

      {/* ── CTA ── */}
      <section style={{ background: C.bgAlt, padding: "0 28px 72px" }}>
        <Reveal className="feat-cta" style={{ maxWidth: 1160, margin: "0 auto", padding: "50px 40px", borderRadius: 24, background: "linear-gradient(135deg, #0560FF 0%, #204493 100%)", color: "#fff", textAlign: "center" }}>
          <div className="feat-orb" style={{ width: 320, height: 320, background: f.color, top: -160, right: -80, opacity: .45 }} />
          <div style={{ position: "relative" }}>
            <h2 style={{ fontFamily: font, fontSize: "clamp(24px,3vw,30px)", fontWeight: 800, letterSpacing: -0.7, margin: "0 0 12px" }}>
              Quer ver {f.name.toLowerCase()} na prática?
            </h2>
            <p style={{ fontFamily: font, fontSize: 16.5, lineHeight: 1.6, color: "rgba(255,255,255,0.85)", margin: "0 auto 26px", maxWidth: 540 }}>
              Agende uma demonstração e veja como o IMOBMEET conecta vendas e financeiro da sua operação em uma tela só.
            </p>
            <Link to="/agendar-demo" className="feat-card" style={{ display: "inline-flex", fontFamily: font, fontSize: 15, fontWeight: 800, color: C.accent, background: "#fff", padding: "15px 32px", borderRadius: 12, textDecoration: "none", boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}>
              Agendar demonstração →
            </Link>
          </div>
        </Reveal>
      </section>

      {/* ── Outras funcionalidades da categoria ── */}
      {others.length > 0 && (
        <section style={{ background: C.surface, padding: "24px 28px 88px" }}>
          <div style={{ maxWidth: 1160, margin: "0 auto" }}>
            <Reveal style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 14, marginBottom: 26 }}>
              <div>
                <span style={{ display: "inline-block", fontFamily: font, fontSize: 11.5, fontWeight: 800, letterSpacing: 1.1, textTransform: "uppercase", color: f.color, marginBottom: 8 }}>
                  Explore também
                </span>
                <h2 style={{ fontFamily: font, fontSize: "clamp(22px,2.6vw,28px)", fontWeight: 800, letterSpacing: -0.6, color: C.text, margin: 0 }}>
                  Outras funcionalidades de {f.category}
                </h2>
              </div>
              <Link to="/funcionalidades" style={{ fontFamily: font, fontSize: 14, fontWeight: 800, color: C.accent, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
                Ver todas as funcionalidades →
              </Link>
            </Reveal>
            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 18 }}>
              {others.map((o, i) => (
                <Reveal key={o.slug} delay={Math.min(i + 1, 6)} style={{ flex: "1 1 300px", maxWidth: 368, display: "flex" }}>
                  <Link to={o.ownPage ? `/funcionalidades/${o.slug}` : o.externalTo} className="feat-more">
                    <span className="feat-tile-bar" style={{ background: o.bg }} />
                    <span style={{ width: 48, height: 48, borderRadius: 14, background: o.bg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 10px 24px ${o.color}44` }}>
                      {o.icon}
                    </span>
                    <span style={{ fontFamily: font, fontSize: 16.5, fontWeight: 800, lineHeight: 1.2, color: C.text, letterSpacing: -0.2 }}>{o.name}</span>
                    {o.tagline && (
                      <span style={{ fontFamily: font, fontSize: 14, lineHeight: 1.5, color: C.muted }}>{o.tagline}</span>
                    )}
                    <span className="go" style={{ fontFamily: font }}>
                      Conhecer
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                    </span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
      <Footer />
    </>
  );
}
